import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Search, Plus, Minus, Trash2, CheckCircle, AlertTriangle, FileText, Sun, Moon, Phone, Grid, List, ChevronLeft, ChevronRight, Zap, Star, Tag, Flame } from 'lucide-react';
import { getProductSalesUnit } from '../lib/productPricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inp = (dk) => `w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500 ${dk ? 'bg-gray-800 text-white border-gray-600 placeholder-gray-400' : 'bg-white text-gray-900 border-gray-300 placeholder-gray-400'}`;

const BADGE_STYLES = {
  PROMOCAO: { bg: 'bg-green-500', text: 'PROMOÇÃO', icon: Tag },
  OFERTA_RELAMPAGO: { bg: 'bg-red-500', text: 'OFERTA', icon: Zap },
  MAIS_VENDIDO: { bg: 'bg-yellow-500', text: 'TOP', icon: Star },
  NOVO: { bg: 'bg-blue-500', text: 'NOVO', icon: Star },
  DESTAQUE: { bg: 'bg-purple-500', text: 'DESTAQUE', icon: Flame }
};

const BannerCarousel = ({ banners }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % banners.length), 4000);
    return () => clearInterval(t);
  }, [banners.length]);
  if (!banners.length) return null;
  const b = banners[idx];
  const isVideo = b.url?.match(/\.(mp4|webm|mov)$/i);
  return (
    <div className="relative rounded-2xl overflow-hidden mx-2 mt-3" style={{ maxHeight: 220 }} data-testid="pedido-banner">
      {isVideo ? (
        <video src={b.url} autoPlay loop muted playsInline className="w-full h-full object-cover" style={{ maxHeight: 220 }} />
      ) : (
        <img src={b.url} alt="" className="w-full object-cover" style={{ maxHeight: 220 }} />
      )}
      {banners.length > 1 && (
        <>
          <button onClick={() => setIdx(i => (i - 1 + banners.length) % banners.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-1.5 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setIdx(i => (i + 1) % banners.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-1.5 rounded-full"><ChevronRight className="w-4 h-4" /></button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {banners.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white w-5' : 'bg-white/50'}`} />)}
          </div>
        </>
      )}
    </div>
  );
};

const ProductBadge = ({ badge }) => {
  const style = BADGE_STYLES[badge];
  if (!style) return null;
  const Icon = style.icon;
  return (
    <span className={`absolute top-1.5 left-1.5 ${style.bg} text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 shadow-lg z-10`}>
      <Icon className="w-2.5 h-2.5" />{style.text}
    </span>
  );
};

export const PedidoVendedor = () => {
  const [token] = useState(() => window.location.pathname.split('/pedido/')[1] || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sellerName, setSellerName] = useState('');
  const [valorMinimo, setValorMinimo] = useState(0);
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [cart, setCart] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [dk, setDk] = useState(false);
  const [logoBase64, setLogoBase64] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('');

  const [form, setForm] = useState({
    cliente_nome: '', cliente_email: '', cliente_whatsapp: '',
    cliente_endereco: '', cliente_cidade: '', cliente_estado: '', cliente_cep: '',
    observacoes: ''
  });

  useEffect(() => {
    if (!token) { setError('Link inválido'); setLoading(false); return; }
    Promise.all([
      axios.get(`${API}/pedido/${token}`),
      axios.get(`${API}/public/settings`).catch(() => ({ data: {} }))
    ]).then(([pedidoRes, settingsRes]) => {
      setSellerName(pedidoRes.data.seller_name);
      setValorMinimo(Number(pedidoRes.data.valor_minimo) || 0);
      setProducts(pedidoRes.data.products);
      setBanners(pedidoRes.data.banners || []);
      setLogoBase64(settingsRes.data?.logo_base64 || '');
      setWhatsappNumber(settingsRes.data?.whatsapp_number || '');
    }).catch(err => {
      const s = err.response?.status;
      if (s === 410) setError('Este link já foi utilizado e não é mais válido.');
      else if (s === 404) setError('Link inválido ou não encontrado.');
      else setError('Erro ao carregar. Tente novamente.');
    }).finally(() => setLoading(false));
  }, [token]);

  const categories = useMemo(() => {
    const cats = {};
    products.forEach(p => { const c = p.categoria || 'Outros'; cats[c] = (cats[c] || 0) + 1; });
    return Object.entries(cats).sort((a, b) => b[1] - a[1]);
  }, [products]);

  const filtered = useMemo(() => products.filter(p => {
    const ms = !search || p.produto?.toLowerCase().includes(search.toLowerCase()) || p.codigo?.toLowerCase().includes(search.toLowerCase());
    const mc = !selectedCat || p.categoria === selectedCat;
    return ms && mc;
  }), [products, search, selectedCat]);

  const getQty = (id) => cart.find(c => c.produto_id === id)?.quantidade || 0;
  const getPrice = (p) => {
    const baseUnit = getProductSalesUnit(p);
    return p.promocao_ativa && p.preco_promocional > 0 ? p.preco_promocional : baseUnit.preco;
  };

  const addToCart = (product) => {
    const price = getPrice(product);
    const unit = getProductSalesUnit(product);
    const existing = cart.find(c => c.produto_id === product.id);
    if (existing) {
      setCart(cart.map(c => c.produto_id === product.id ? { ...c, quantidade: c.quantidade + 1 } : c));
    } else {
      setCart([...cart, {
        produto_id: product.id, produto_nome: product.produto, quantidade: 1,
        valor_unitario: price, foto: product.fotos?.[0] || null, desconto_percentual: 0, comissao_percentual: 0,
        tipo_entrada: unit.tipo  // 'caixa' ou 'pacote'
      }]);
    }
  };
  const decreaseFromCart = (pid) => {
    const item = cart.find(c => c.produto_id === pid);
    if (!item) return;
    if (item.quantidade <= 1) setCart(cart.filter(c => c.produto_id !== pid));
    else setCart(cart.map(c => c.produto_id === pid ? { ...c, quantidade: c.quantidade - 1 } : c));
  };
  const setQty = (idx, qty) => {
    const val = Math.max(0, parseInt(qty) || 0);
    if (val === 0) setCart(cart.filter((_, i) => i !== idx));
    else setCart(cart.map((c, i) => i !== idx ? c : { ...c, quantidade: val }));
  };
  const total = cart.reduce((sum, item) => sum + (item.quantidade * item.valor_unitario), 0);

  const handleSubmit = async () => {
    if (!form.cliente_nome.trim()) { alert('Preencha o nome do cliente!'); return; }
    if (cart.length === 0) { alert('Adicione pelo menos um produto!'); return; }
    if (valorMinimo > 0 && total < valorMinimo) {
      alert(`Pedido mínimo de ${fmt(valorMinimo)}. Faltam ${fmt(valorMinimo - total)} para atingir o valor mínimo.`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/pedido/${token}`, { ...form, items: cart });
      setSuccess(res.data);
    } catch (err) { alert(err.response?.data?.detail || 'Erro ao enviar pedido'); }
    setSubmitting(false);
  };

  const bg = dk ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const txt = dk ? 'text-white' : 'text-gray-900';
  const txtSub = dk ? 'text-gray-400' : 'text-gray-500';

  if (loading) return <div className={`min-h-screen ${bg} flex items-center justify-center`}><div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return (
    <div className={`min-h-screen ${bg} flex items-center justify-center p-4`}>
      <div className={`rounded-2xl shadow-lg p-8 max-w-md w-full text-center border ${cardBg}`} data-testid="link-error">
        <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className={`text-2xl font-bold mb-2 ${txt}`}>Link Indisponível</h1>
        <p className={txtSub}>{error}</p>
      </div>
    </div>
  );
  if (success) return (
    <div className={`min-h-screen ${bg} flex items-center justify-center p-4`}>
      <div className={`rounded-2xl shadow-lg p-8 max-w-md w-full text-center border ${cardBg}`} data-testid="order-success">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className={`text-2xl font-bold mb-2 ${txt}`}>Pedido Enviado!</h1>
        <p className={txtSub}>Pedido <strong>#{success.numero_pedido}</strong> criado.</p>
        <a href={`${BACKEND_URL}/api/romaneios/${success.romaneio_id}/pdf`} target="_blank" rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-600">
          <FileText className="w-5 h-5" /> Baixar PDF
        </a>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${bg}`} data-testid="pedido-vendedor-page">
      {/* Header */}
      <header className={`shadow-sm sticky top-0 z-30 border-b ${dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoBase64 && <img src={logoBase64} alt="Logo" className="h-9 w-auto" />}
            <div>
              <h1 className={`text-base font-bold ${txt}`}>Novo Pedido</h1>
              <p className="text-xs text-orange-600">Vendedor: <strong>{sellerName}</strong></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {whatsappNumber && (
              <a href={`https://wa.me/55${whatsappNumber.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-full text-xs font-medium">
                <Phone className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
            <button onClick={() => setDk(!dk)} className={`p-1.5 rounded-full ${dk ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-gray-600'}`}>
              {dk ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <div className="text-right ml-1">
              <span className={`text-xs ${txtSub}`}>{cart.length} itens</span>
              <p className="font-bold text-orange-600">{fmt(total)}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Banner Carrossel */}
      <div className="max-w-5xl mx-auto px-2">
        <BannerCarousel banners={banners} />
      </div>

      {/* Aviso de Pedido Mínimo */}
      {valorMinimo > 0 && (
        <div className="max-w-5xl mx-auto px-4 mt-3">
          <div
            data-testid="valor-minimo-banner"
            className={`rounded-xl border-2 px-4 py-3 flex items-center justify-between gap-3 ${
              total >= valorMinimo
                ? 'border-green-400 bg-green-50 text-green-800'
                : 'border-orange-400 bg-orange-50 text-orange-800'
            }`}
          >
            <div className="flex items-center gap-2 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Pedido mínimo: <strong>{fmt(valorMinimo)}</strong></span>
            </div>
            <div className="text-xs sm:text-sm">
              {total >= valorMinimo
                ? <span className="font-bold">Mínimo atingido</span>
                : <span>Faltam <strong>{fmt(valorMinimo - total)}</strong></span>}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        {/* Dados do Cliente + Endereço */}
        <div className={`rounded-xl shadow-sm p-4 border ${cardBg}`}>
          <h2 className={`font-bold text-sm mb-3 ${txt}`}>Dados do Cliente</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            <input type="text" value={form.cliente_nome} onChange={e => setForm({...form, cliente_nome: e.target.value})}
              placeholder="Nome completo *" className={inp(dk)} data-testid="cliente-nome-input" />
            <input type="text" value={form.cliente_whatsapp} onChange={e => setForm({...form, cliente_whatsapp: e.target.value})}
              placeholder="WhatsApp" className={inp(dk)} />
            <input type="email" value={form.cliente_email} onChange={e => setForm({...form, cliente_email: e.target.value})}
              placeholder="Email" className={inp(dk)} />
            <input type="text" value={form.cliente_endereco} onChange={e => setForm({...form, cliente_endereco: e.target.value})}
              placeholder="Endereço (Rua, Nº)" className={`${inp(dk)} sm:col-span-2`} />
            <input type="text" value={form.cliente_cidade} onChange={e => setForm({...form, cliente_cidade: e.target.value})}
              placeholder="Cidade" className={inp(dk)} />
            <input type="text" value={form.cliente_estado} onChange={e => setForm({...form, cliente_estado: e.target.value})}
              placeholder="Estado (UF)" className={inp(dk)} />
            <input type="text" value={form.cliente_cep} onChange={e => setForm({...form, cliente_cep: e.target.value})}
              placeholder="CEP" className={inp(dk)} />
          </div>
        </div>

        {/* Catálogo */}
        <div className={`rounded-xl shadow-sm p-4 border ${cardBg}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className={`font-bold text-sm ${txt}`}>Produtos</h2>
            <div className="flex gap-1">
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-orange-100 text-orange-600' : txtSub}`}><Grid className="w-4 h-4" /></button>
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-orange-100 text-orange-600' : txtSub}`}><List className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input type="text" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)}
                className={`pl-9 pr-3 py-2 text-sm w-full border rounded-lg outline-none ${dk ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-900 border-gray-200'}`} />
            </div>
            <select value={selectedCat} onChange={e => setSelectedCat(e.target.value)}
              className={`px-2 py-2 text-xs border rounded-lg ${dk ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-900 border-gray-200'}`}>
              <option value="">Todas</option>
              {categories.map(([cat, count]) => <option key={cat} value={cat}>{cat} ({count})</option>)}
            </select>
          </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[450px] overflow-y-auto pr-1">
              {filtered.slice(0, 60).map(p => {
                const qty = getQty(p.id);
                const preco = getPrice(p);
                const unit = getProductSalesUnit(p);
                const precoOriginal = unit.preco;
                const temPromo = p.promocao_ativa && p.preco_promocional > 0;
                return (
                  <div key={p.id} className={`border-2 rounded-xl overflow-hidden transition-all relative ${qty > 0 ? 'border-orange-500 shadow-md shadow-orange-100' : dk ? 'border-gray-700' : 'border-gray-200'}`}>
                    <ProductBadge badge={p.badge} />
                    <div className={`aspect-square ${dk ? 'bg-gray-700' : 'bg-gray-100'} flex items-center justify-center overflow-hidden cursor-pointer`}
                      onClick={() => addToCart(p)}>
                      {p.fotos?.[0] ? <img src={p.fotos[0]} alt="" className="w-full h-full object-cover" /> :
                        <span className={`text-4xl font-bold ${dk ? 'text-gray-500' : 'text-gray-300'}`}>{(p.produto || 'P')[0]}</span>}
                      {qty > 0 && <div className="absolute top-1.5 right-1.5 w-7 h-7 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow z-10">{qty}</div>}
                    </div>
                    <div className="p-2">
                      <p className={`text-xs font-semibold leading-tight truncate ${txt}`}>{p.produto}</p>
                      <p className={`text-[10px] ${txtSub} truncate`}>{p.codigo}</p>
                      <div className="mt-1">
                        {temPromo ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-400 line-through">{fmt(precoOriginal)}</span>
                            <span className="text-sm font-bold text-red-600">{fmt(preco)}</span>
                          </div>
                        ) : (
                          <span className="text-sm font-bold text-orange-600">{fmt(preco)}</span>
                        )}
                        <span className={`text-[10px] block ${txtSub}`}>{unit.label}</span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <button onClick={() => decreaseFromCart(p.id)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center border-2 transition-all ${qty > 0 ? 'border-orange-500 text-orange-600 hover:bg-orange-50' : dk ? 'border-gray-600 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className={`text-sm font-bold min-w-[24px] text-center ${txt}`}>{qty}</span>
                        <button onClick={() => addToCart(p)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center border-2 border-orange-500 text-orange-600 hover:bg-orange-50 transition-all">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1 max-h-[450px] overflow-y-auto pr-1">
              {filtered.slice(0, 60).map(p => {
                const qty = getQty(p.id);
                const preco = getPrice(p);
                const unit = getProductSalesUnit(p);
                const precoOriginal = unit.preco;
                const temPromo = p.promocao_ativa && p.preco_promocional > 0;
                return (
                  <div key={p.id} className={`flex items-center gap-3 p-2 rounded-lg border-2 transition-all relative ${qty > 0 ? 'border-orange-500' + (dk ? ' bg-orange-900/20' : ' bg-orange-50') : dk ? 'border-gray-700' : 'border-gray-100'}`}>
                    {p.badge && <span className={`absolute -top-1 -left-1 ${BADGE_STYLES[p.badge]?.bg || 'bg-gray-500'} text-white text-[8px] font-bold px-1.5 py-0.5 rounded-md z-10`}>{BADGE_STYLES[p.badge]?.text || ''}</span>}
                    <div className={`w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 ${dk ? 'bg-gray-700' : 'bg-gray-100'}`} onClick={() => addToCart(p)}>
                      {p.fotos?.[0] ? <img src={p.fotos[0]} alt="" className="w-full h-full object-cover" /> :
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm font-bold">{(p.produto || 'P')[0]}</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${txt}`}>{p.produto}</p>
                      <p className={`text-[10px] ${txtSub}`}>{p.codigo} • {unit.label}</p>
                    </div>
                    {temPromo ? (
                      <div className="text-right flex-shrink-0">
                        <span className="text-[10px] text-gray-400 line-through block">{fmt(precoOriginal)}</span>
                        <span className="text-sm font-bold text-red-600">{fmt(preco)}</span>
                      </div>
                    ) : (
                      <span className="text-sm font-bold text-orange-600 flex-shrink-0">{fmt(preco)}</span>
                    )}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => decreaseFromCart(p.id)} className={`w-7 h-7 rounded-lg flex items-center justify-center border-2 ${qty > 0 ? 'border-orange-500 text-orange-600' : dk ? 'border-gray-600 text-gray-500' : 'border-gray-300 text-gray-400'}`}><Minus className="w-3 h-3" /></button>
                      <span className={`w-6 text-center text-sm font-bold ${txt}`}>{qty}</span>
                      <button onClick={() => addToCart(p)} className="w-7 h-7 rounded-lg flex items-center justify-center border-2 border-orange-500 text-orange-600"><Plus className="w-3 h-3" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Carrinho */}
        {cart.length > 0 && (
          <div className={`rounded-xl shadow-sm p-4 border ${cardBg}`}>
            <h2 className={`font-bold text-sm mb-3 ${txt}`}>Carrinho ({cart.length})</h2>
            <div className="space-y-2">
              {cart.map((item, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-2 rounded-lg border-2 border-orange-200 ${dk ? 'bg-gray-900' : 'bg-orange-50/50'}`}>
                  <div className={`w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 ${dk ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    {item.foto ? <img src={item.foto} alt="" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm font-bold">{(item.produto_nome || 'P')[0]}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${txt}`}>{item.produto_nome}</p>
                    <p className={`text-xs ${txtSub}`}>{fmt(item.valor_unitario)}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setQty(idx, item.quantidade - 1)} className="w-8 h-8 rounded-lg flex items-center justify-center border-2 border-orange-400 text-orange-600"><Minus className="w-4 h-4" /></button>
                    <input type="number" value={item.quantidade} onChange={e => setQty(idx, e.target.value)}
                      className={`w-12 text-center text-sm font-bold border-2 border-orange-300 rounded-lg py-1 outline-none ${dk ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`} />
                    <button onClick={() => setQty(idx, item.quantidade + 1)} className="w-8 h-8 rounded-lg flex items-center justify-center border-2 border-orange-400 text-orange-600"><Plus className="w-4 h-4" /></button>
                  </div>
                  <span className={`text-sm font-bold w-20 text-right ${txt}`}>{fmt(item.quantidade * item.valor_unitario)}</span>
                  <button onClick={() => setCart(cart.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Observações + Enviar */}
        <div className={`rounded-xl shadow-sm p-4 border ${cardBg}`}>
          <input type="text" value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})}
            placeholder="Observações (opcional)" className={`${inp(dk)} mb-3`} />
          <div className={`flex items-center justify-between pt-3 border-t ${dk ? 'border-gray-600' : 'border-gray-200'}`}>
            <p className={`text-xl font-bold ${txt}`}>Total: <span className="text-orange-600">{fmt(total)}</span></p>
            <button onClick={handleSubmit} disabled={submitting || cart.length === 0 || (valorMinimo > 0 && total < valorMinimo)}
              className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg font-bold transition-colors flex items-center gap-2"
              data-testid="submit-order-btn"
              title={valorMinimo > 0 && total < valorMinimo ? `Pedido mínimo: ${fmt(valorMinimo)}` : ''}>
              {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle className="w-5 h-5" />}
              {submitting ? 'Enviando...' : (valorMinimo > 0 && total < valorMinimo ? `Falta ${fmt(valorMinimo - total)}` : 'Enviar Pedido')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PedidoVendedor;
